import { useSyncExternalStore } from 'react';
import { track } from '@vercel/analytics';

export interface ConsentSnapshot {
  advertising: boolean | null;
  analytics: boolean | null;
  decided: boolean;
  consent_at: string | null;
  policy_version: 'acquisition-v1' | 'acquisition-v1-termly-opt-out';
}
const unknown: ConsentSnapshot = { advertising: null, analytics: null, decided: false, consent_at: null,
  policy_version: 'acquisition-v1' };
const key = 'bb_consent_preferences_v1';
const listeners = new Set<() => void>();
let snapshot = unknown;
const bound = new WeakSet<object>();

function publish(next: ConsentSnapshot) {
  if (JSON.stringify(snapshot) === JSON.stringify(next)) return;
  snapshot = next;
  listeners.forEach(listener => listener());
}

export function initializeConsent() {
  try {
    const saved = JSON.parse(localStorage.getItem(key) || 'null');
    if (saved?.site === import.meta.env.VITE_TERMLY_SITE_ID && saved?.version === 1
      && saved.expires > Date.now() && typeof saved.advertising === 'boolean' && typeof saved.analytics === 'boolean'
      && Number.isFinite(Date.parse(saved.consent_at))) {
      publish({ advertising: saved.advertising, analytics: saved.analytics, decided: true,
        consent_at: saved.consent_at, policy_version: 'acquisition-v1' });
    } else publish(unknown);
  } catch { publish(unknown); }
}

export function bindTermlyConsent() {
  const termly = window.Termly;
  if (!termly || bound.has(termly)) return;
  bound.add(termly);
  let optOut = false;
  const read = (decided: boolean) => {
    const state = termly.getConsentState();
    const hasChoice = decided || (snapshot.decided && snapshot.policy_version === 'acquisition-v1');
    const resolved = hasChoice || optOut;
    const next: ConsentSnapshot = { advertising: resolved ? state.advertising === true : null,
      analytics: resolved ? state.analytics === true : null, decided: resolved,
      consent_at: decided || (optOut && !hasChoice) ? new Date().toISOString() : snapshot.consent_at,
      policy_version: hasChoice ? 'acquisition-v1' : 'acquisition-v1-termly-opt-out' };
    publish(next);
    // Regional defaults are current CMP permission, not a choice to remember for 180 days.
    if (hasChoice) {
      try {
        localStorage.setItem(key, JSON.stringify({ ...next, version: 1,
          site: import.meta.env.VITE_TERMLY_SITE_ID, expires: Date.parse(next.consent_at!) + 180 * 86400000 }));
      } catch { /* Consent still applies in memory when storage is unavailable. */ }
    }
  };
  termly.on('initialized', data => {
    optOut = data?.region_settings?.consent_mode === 'opt_out';
    read(false);
  });
  termly.on('consent', () => read(true));
}

export function subscribeConsent(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function getConsentSnapshot() { return snapshot; }
export function useConsent() {
  return useSyncExternalStore(subscribeConsent, getConsentSnapshot, () => unknown);
}
export function trackFunnel(event: 'landing_cta' | 'signup_view' | 'signup_submit' | 'oauth_start') {
  if (snapshot.analytics === true) track(event);
}
